// fieldPickerChrome — the shared vocabulary for the studio's field pickers.
//
// WHY THIS FILE EXISTS
// The studio has two field pickers with different data sources:
//   • FieldPickerModal (activity-triggers) — takes prebuilt `groups`, fields
//     keyed { id, name }.
//   • TemplateFieldSelector (FlexibleLayout) — derives groups from a flat field
//     list + Redux, fields keyed { key_name, label }.
// They are not one component and should not become one: their row internals
// genuinely differ (drill-stack and lookup/phone/user/grid/node-status branches
// on one side, sub_columns expansion and rv/grid column modes on the other).
//
// But they must LOOK like one product. Previously TemplateFieldSelector reached
// into FieldPickerModal for `getTypeStyle`, which made one picker depend on the
// other and let the two drift apart every time either changed. Both now depend
// on THIS module instead, and on nothing of each other's.
//
// WHAT BELONGS HERE
// Presentation that carries no knowledge of either data shape: colour tokens,
// the data-type palette, section/folder chrome, the type badge. Every component
// below takes primitives or ReactNode slots — never a field or group object —
// so neither picker's field keys can leak in.
//
// WHAT DOES NOT BELONG HERE
// Group building, field filtering, selection semantics, fetches. Those are each
// picker's own business and differ between them.

import { ChevronRight, ChevronDown, FolderOpen, Database, Hash, Type, Calendar, ToggleLeft, List, Phone, User, Globe, Settings2 } from 'lucide-react';

// ─── Data-type palette ──────────────────────────────────────────────────
// Moved here from FieldPickerModal verbatim — same keys, same classes, so no
// picker changes appearance from the move. `dot` is retained because the
// type-filter legend still uses it as its colour key even though the field rows
// no longer render a dot.
const dataTypeStyles = {
    string:  { icon: Type,        bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500', ring: 'ring-emerald-500/30' },
    text:    { icon: Type,        bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500', ring: 'ring-emerald-500/30' },
    longtext:{ icon: Type,        bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500', ring: 'ring-emerald-500/30' },
    number:  { icon: Hash,        bg: 'bg-blue-50 dark:bg-blue-900/20',      text: 'text-blue-600 dark:text-blue-400',      dot: 'bg-blue-500',    ring: 'ring-blue-500/30' },
    integer: { icon: Hash,        bg: 'bg-blue-50 dark:bg-blue-900/20',      text: 'text-blue-600 dark:text-blue-400',      dot: 'bg-blue-500',    ring: 'ring-blue-500/30' },
    float:   { icon: Hash,        bg: 'bg-blue-50 dark:bg-blue-900/20',      text: 'text-blue-600 dark:text-blue-400',      dot: 'bg-blue-500',    ring: 'ring-blue-500/30' },
    currency:{ icon: Hash,        bg: 'bg-blue-50 dark:bg-blue-900/20',      text: 'text-blue-600 dark:text-blue-400',      dot: 'bg-blue-500',    ring: 'ring-blue-500/30' },
    boolean: { icon: ToggleLeft,  bg: 'bg-amber-50 dark:bg-amber-900/20',    text: 'text-amber-600 dark:text-amber-400',    dot: 'bg-amber-500',   ring: 'ring-amber-500/30' },
    date:    { icon: Calendar,    bg: 'bg-purple-50 dark:bg-purple-900/20',  text: 'text-purple-600 dark:text-purple-400',  dot: 'bg-purple-500',  ring: 'ring-purple-500/30' },
    datetime:{ icon: Calendar,    bg: 'bg-purple-50 dark:bg-purple-900/20',  text: 'text-purple-600 dark:text-purple-400',  dot: 'bg-purple-500',  ring: 'ring-purple-500/30' },
    time:    { icon: Calendar,    bg: 'bg-purple-50 dark:bg-purple-900/20',  text: 'text-purple-600 dark:text-purple-400',  dot: 'bg-purple-500',  ring: 'ring-purple-500/30' },
    daterange:{ icon: Calendar,   bg: 'bg-purple-50 dark:bg-purple-900/20',  text: 'text-purple-600 dark:text-purple-400',  dot: 'bg-purple-500',  ring: 'ring-purple-500/30' },
    select:  { icon: List,        bg: 'bg-sky-50 dark:bg-sky-900/20',        text: 'text-sky-600 dark:text-sky-400',        dot: 'bg-sky-500',     ring: 'ring-sky-500/30' },
    multiselect:{ icon: List,     bg: 'bg-sky-50 dark:bg-sky-900/20',        text: 'text-sky-600 dark:text-sky-400',        dot: 'bg-sky-500',     ring: 'ring-sky-500/30' },
    radio:   { icon: List,        bg: 'bg-sky-50 dark:bg-sky-900/20',        text: 'text-sky-600 dark:text-sky-400',        dot: 'bg-sky-500',     ring: 'ring-sky-500/30' },
    checkbox:{ icon: List,        bg: 'bg-sky-50 dark:bg-sky-900/20',        text: 'text-sky-600 dark:text-sky-400',        dot: 'bg-sky-500',     ring: 'ring-sky-500/30' },
    // Both learning types style as the choice family they belong to.
    learning_text: { icon: List,  bg: 'bg-sky-50 dark:bg-sky-900/20',        text: 'text-sky-600 dark:text-sky-400',        dot: 'bg-sky-500',     ring: 'ring-sky-500/30' },
    learning_tags: { icon: List,  bg: 'bg-sky-50 dark:bg-sky-900/20',        text: 'text-sky-600 dark:text-sky-400',        dot: 'bg-sky-500',     ring: 'ring-sky-500/30' },
    array:   { icon: List,        bg: 'bg-rose-50 dark:bg-rose-900/20',      text: 'text-rose-600 dark:text-rose-400',      dot: 'bg-rose-500',    ring: 'ring-rose-500/30' },
    list:    { icon: List,        bg: 'bg-rose-50 dark:bg-rose-900/20',      text: 'text-rose-600 dark:text-rose-400',      dot: 'bg-rose-500',    ring: 'ring-rose-500/30' },
    grid:    { icon: List,        bg: 'bg-rose-50 dark:bg-rose-900/20',      text: 'text-rose-600 dark:text-rose-400',      dot: 'bg-rose-500',    ring: 'ring-rose-500/30' },
    lookup:  { icon: Database,    bg: 'bg-indigo-50 dark:bg-indigo-900/20',  text: 'text-indigo-600 dark:text-indigo-400',  dot: 'bg-indigo-500',  ring: 'ring-indigo-500/30' },
    wf_lookup:{ icon: Database,   bg: 'bg-indigo-50 dark:bg-indigo-900/20',  text: 'text-indigo-600 dark:text-indigo-400',  dot: 'bg-indigo-500',  ring: 'ring-indigo-500/30' },
    phone:   { icon: Phone,       bg: 'bg-teal-50 dark:bg-teal-900/20',      text: 'text-teal-600 dark:text-teal-400',      dot: 'bg-teal-500',    ring: 'ring-teal-500/30' },
    app_user:     { icon: Database, bg: 'bg-sky-50 dark:bg-sky-900/20',      text: 'text-sky-600 dark:text-sky-400',        dot: 'bg-sky-500',     ring: 'ring-sky-500/30' },
    performed_by: { icon: Database, bg: 'bg-sky-50 dark:bg-sky-900/20',      text: 'text-sky-600 dark:text-sky-400',        dot: 'bg-sky-500',     ring: 'ring-sky-500/30' },
    file:    { icon: Database,    bg: 'bg-orange-50 dark:bg-orange-900/20',  text: 'text-orange-600 dark:text-orange-400',  dot: 'bg-orange-500',  ring: 'ring-orange-500/30' },
    image:   { icon: Database,    bg: 'bg-orange-50 dark:bg-orange-900/20',  text: 'text-orange-600 dark:text-orange-400',  dot: 'bg-orange-500',  ring: 'ring-orange-500/30' },
};

const defaultTypeStyle = { icon: Database, bg: 'bg-slate-50 dark:bg-slate-800', text: 'text-slate-500 dark:text-slate-400', dot: 'bg-slate-400', ring: 'ring-slate-400/30' };

export const getTypeStyle = (dataType) => {
    if (!dataType) return defaultTypeStyle;
    const key = dataType.toLowerCase().trim();
    return dataTypeStyles[key] || defaultTypeStyle;
};

// ─── Panel size ─────────────────────────────────────────────────────────
// One width for every field picker, whichever shell hosts it. FieldPickerModal
// sets it directly; the shells wrapping TemplateFieldSelector go through
// common/Modal's `picker` size, which resolves to PICKER_PANEL_MAX_W. Keeping
// the number here means a change lands on both pickers at once instead of
// drifting one shell at a time.
export const PICKER_PANEL_WIDTH_PX = 360;
export const PICKER_PANEL_W = 'w-[360px]';
export const PICKER_PANEL_MAX_W = 'max-w-[360px]';
// For shells that size via inline style rather than a Tailwind class —
// BuilderModal takes a `maxWidth` string and defaults to 640px, which made every
// picker it hosts nearly twice the width of FieldPickerModal.
export const PICKER_PANEL_WIDTH_CSS = '360px';

// ─── Sections ───────────────────────────────────────────────────────────
// Folders split into platform-level SOURCES (logged-in user, system fields,
// global fields) and the workflow's own ACTIVITIES. Each picker decides which
// of its folders is which — FieldPickerModal from `sourceType`,
// TemplateFieldSelector from its bucket key — and this module only names and
// draws the split.
export const PICKER_SECTION = Object.freeze({
    SOURCE: 'source',
    ACTIVITY: 'activity',
});

export const PICKER_SECTION_LABELS = Object.freeze({
    [PICKER_SECTION.SOURCE]: 'Sources',
    [PICKER_SECTION.ACTIVITY]: 'Activities',
});

// ─── Folder colours ─────────────────────────────────────────────────────
// Colour carries MEANING, not position. Both pickers previously rotated a
// palette across activities, which made the list look categorised when the hue
// said nothing at all. Activities are blue; the INIT activity is emerald,
// matching InitNode on the workflow canvas, so the entry point is the one
// folder that stands out.
export const ACTIVITY_COLORS = Object.freeze({ iconColor: 'text-blue-500', iconBg: 'bg-blue-100 dark:bg-blue-900/30' });
export const INIT_ACTIVITY_COLORS = Object.freeze({ iconColor: 'text-emerald-500', iconBg: 'bg-emerald-100 dark:bg-emerald-900/30' });

export const SOURCE_COLORS = Object.freeze({
    user:   { iconColor: 'text-emerald-500', iconBg: 'bg-emerald-100 dark:bg-emerald-900/30' },
    system: { iconColor: 'text-amber-500',   iconBg: 'bg-amber-100 dark:bg-amber-900/30' },
    global: { iconColor: 'text-violet-500',  iconBg: 'bg-violet-100 dark:bg-violet-900/30' },
});

// Per-category folder glyphs. Activities keep FolderOpen; a source folder gets
// an icon that says what kind of source it is, which is what made the flat
// all-FolderOpen list unreadable.
export const SOURCE_ICONS = Object.freeze({
    user: User,
    system: Settings2,
    global: Globe,
});

// `type` is the Redux spelling, `activity_type` the API one — both are in play
// across call sites, so accept either (the same test the Invoke / Schedule /
// StartWorkflow configs use).
export const isInitActivity = (act) => (act?.type || act?.activity_type) === 'INIT';
export const activityFolderColors = (act) => (isInitActivity(act) ? INIT_ACTIVITY_COLORS : ACTIVITY_COLORS);

// ─── Chrome ─────────────────────────────────────────────────────────────

// Sticky heading above the first folder of a section. Callers render it only on
// that first folder, and should derive "first" from their FILTERED list so a
// search never strands a heading above zero folders.
export const PickerSectionHeading = ({ section, label }) => (
    <div className="flex items-center gap-2 px-3.5 pt-3 pb-1.5 bg-white dark:bg-slate-900">
        <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 grey:text-zinc-500">
            {label || PICKER_SECTION_LABELS[section] || section}
        </span>
        <span className="flex-1 h-px bg-slate-100 dark:bg-slate-800 grey:bg-zinc-200" aria-hidden />
    </div>
);

// Folder header. `icon` defaults to FolderOpen so an activity folder needs to
// pass nothing; `trailing` is a slot for per-picker extras.
export const PickerFolderHeader = ({
    label,
    icon: Icon = FolderOpen,
    iconBg,
    iconColor,
    isCollapsed,
    onToggle,
    trailing,
}) => (
    <button
        onClick={onToggle}
        // Stable hook so a test can open folders without guessing at classes or
        // relying on the label (two activity folders can share a label when
        // activity names haven't resolved).
        data-picker-folder
        className="w-full flex items-center gap-2 px-3.5 py-2.5 bg-slate-50/80 dark:bg-slate-800/40 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800/70 transition-colors sticky top-0 z-10"
    >
        <div className={`w-5 h-5 rounded-md flex items-center justify-center ${iconBg || 'bg-slate-100 dark:bg-slate-800/30'}`}>
            <Icon size={11} className={iconColor || 'text-slate-400'} />
        </div>
        <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider flex-1 text-left">
            {label}
        </span>
        {trailing}
        {isCollapsed
            ? <ChevronRight size={12} className="text-slate-400" />
            : <ChevronDown size={12} className="text-slate-400" />
        }
    </button>
);

// Folder body: indents its children as a block and draws a vertical guide rail,
// so a field reads as a CHILD of its folder rather than a sibling. Indenting
// here rather than on each row is deliberate — every row branch keeps its own
// relative indent, so nested sub-columns and sub-folders shift with the block
// and stay one level deeper.
export const PickerFolderBody = ({ children }) => (
    <div className="relative">
        <div
            className="absolute left-[18px] top-0 bottom-0 w-px bg-slate-200/70 dark:bg-slate-700/50 grey:bg-zinc-300/70"
            aria-hidden
        />
        <div className="pl-3">{children}</div>
    </div>
);

// The data-type badge. Rendered as the LAST element of a row by every caller so
// its right edge lands on the row's padding — the type column reads flush right
// the way the label reads flush left.
export const PickerTypeBadge = ({ dataType, className = '' }) => {
    if (!dataType) return null;
    const ts = getTypeStyle(dataType);
    return (
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-md flex-shrink-0 whitespace-nowrap ${ts.bg} ${ts.text} ${className}`}>
            {dataType}
        </span>
    );
};
